/**
 * WatchlistPillView — one "saved in watchlists" pill.
 *
 * Renders as a tappable Pressable when it is handed a press handler (the mobile
 * 2+ list case), otherwise as a static Stack (the desktop indicator and the
 * mobile single-list case). Both share the same chrome so the visual treatment
 * is identical regardless of interactivity.
 * @module features/player-detail/components/molecules/WatchlistPill/WatchlistPillView
 */

import React, { memo } from "react";

import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/**
 * Pill chrome shared by the desktop "Saved in watchlists" indicator and the
 * mobile pill row. Per the V1.6 Figma tokens: neutral pill background, full
 * radius, caption label.
 */
const WATCHLIST_PILL_CLASS =
  "rounded-full bg-surface-muted px-3 py-1 active:opacity-60";

/** Props for {@link WatchlistPillView}. */
export interface WatchlistPillViewProps {
  /** Watchlist id, used for the testID. */
  readonly id: string;
  /** Watchlist display name. */
  readonly name: string;
  /**
   * Tap handler, already bound to this pill's watchlist by the Container, or
   * null when the pill is not interactive.
   */
  readonly onPress: (() => void) | null;
}

/**
 * Renders one watchlist pill.
 * @param root0 - Props
 * @param root0.id - Watchlist id, used for the testID
 * @param root0.name - Watchlist display name
 * @param root0.onPress - Tap handler, or null when non-interactive
 * @returns The pill.
 */
const WatchlistPillView = ({ id, name, onPress }: WatchlistPillViewProps) =>
  onPress === null ? (
    <Stack
      testID={`watchlist-pill-${id}`}
      className={`${WATCHLIST_PILL_CLASS} items-center`}
    >
      <Text variant="caption" className="text-content-primary">
        {name}
      </Text>
    </Stack>
  ) : (
    <Pressable
      testID={`watchlist-pill-${id}`}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`Edit ${name} watchlist membership`}
      className={`${WATCHLIST_PILL_CLASS} items-center`}
    >
      <Text variant="caption" className="text-content-primary">
        {name}
      </Text>
    </Pressable>
  );

WatchlistPillView.displayName = "WatchlistPillView";

export default memo(WatchlistPillView);
